import { DatePipe } from '@angular/common';
import {
	ChangeDetectorRef,
	Component, ElementRef, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UADetect as UADetectClass, uaDetectToken } from 'go-modules/detect/ua-detect.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { MenuComponent } from '@go-modules/components/menus/menu/menu.component';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import { FIRST_COMMENT_TOUR_CLASSES } from '@go-modules/services/go-pulse-tour/constants/first-comment-tour';
import { BadgeColors } from '@go-modules/components/badge/badge.constants';
import { Session as SessionModel } from 'go-modules/models/session/session.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { ActiveBrandService } from '@go-modules/services/active-brand';
import type { BrandImage } from '@go-modules/interfaces/brands/brand.interface';

type SessionInstance = ReturnType<typeof SessionModel>;

@Component({
	selector: 'ngx-session-card',
	templateUrl: './session-card.component.html',
	styleUrls: ['./session-card.component.scss'],
	providers: [DatePipe],
	standalone: false
})
export class SessionCardComponent implements OnChanges {
	public FirstCommentTourClasses = FIRST_COMMENT_TOUR_CLASSES;

	@Output() public goToSession = new EventEmitter<SessionInstance>();
	@Output() public selectionToggled = new EventEmitter<SessionInstance>();
	@ViewChild(MenuComponent)
	public menuComponent: MenuComponent;
	@ViewChild('cardContainer', {read: ElementRef})
	public cardContainer: ElementRef;

	@Input() public session: SessionInstance;
	@Input() public submitMode: boolean = false;
	@Input() public isSelected: boolean = false;
	@Input() public allowSelection = true;
	@Input() public isSubmittingSessions = false;

	public nameList: string[] = [];
	public sessionDescriptionTruncated = null;

	protected BadgeColors = BadgeColors;

	constructor (
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(uaDetectToken) private UADetect: UADetectClass,
		private featureFlag: NgxFeatureFlagService,
		private translateService: TranslateService,
		private datePipe: DatePipe,
		public ngxSessionService: NgxSessionService,
		private cdr: ChangeDetectorRef,
		private activeBrandService: ActiveBrandService
	) {}

	public get spotLogo (): BrandImage {
		return this.activeBrandService.getSpotLogo();
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.session && this.session) {
			this.nameList = this.session.presenters.map((item) => item.fullname);
		}
	}

	private sessionCanBeSubmitted (): boolean {
		const activity = this.selectedService.getActivity();
		if (!this.submitMode || !(activity?.show_tasks || this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG')) || !this.session?.isRecorded?.()) {
			return false;
		}

		const media = this.ngxSessionService.getMedia(this.session);
		return !!media && !media.isPending?.();
	}

	public isSubmitCheckboxActive (): boolean {
		return this.sessionCanBeSubmitted() &&
			(this.session?.isPosted?.() || this.allowSelection);
	}

	public isSubmitOverlayActive (): boolean {
		return this.sessionCanBeSubmitted() && this.allowSelection;
	}

	public shouldShowTask (): boolean {
		return this.selectedService.getActivity()?.show_tasks ||
			this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG');
	}

	public shouldShowSubmissionBadge (): boolean {
		return this.submitMode &&
			this.shouldShowTask() &&
			this.session.isPosted();
	}

	private handleSubmitSelection (event: Event): void {
		if (this.submitMode && this.shouldShowTask()) {
			event.preventDefault();
			event.stopPropagation();
			if (this.allowSelection && this.isSubmitCheckboxActive()) {
				this.selectionToggled.emit(this.session);
			}
			return;
		}
		this.goToSession.emit(this.session);
	}

	public onCardClick (event: MouseEvent): void {
		this.handleSubmitSelection(event);
	}

	public onCardKeyEnter (event: KeyboardEvent): void {
		this.handleSubmitSelection(event);
	}

	public handleCheckboxChanges (change: MatCheckboxChange): void {
		if (change.checked === this.isSelected) {
			return;
		}
		if (!this.allowSelection || this.isSubmittingSessions) {
			change.source.checked = this.isSelected;
			return;
		}
		this.selectionToggled.emit(this.session);
	}

	public onSelectionToggle (event: Event): void {
		if (this.submitMode && this.shouldShowTask()) {
			event.preventDefault();
			event.stopPropagation();
			if (this.isSubmitCheckboxActive()) {
				this.selectionToggled.emit(this.session);
			}
			return;
		}
	}

	public openMenu (event: MouseEvent) {
		event.stopPropagation();
		// open browser menu on shift right click
		if (event.button === 2 && event.shiftKey) {
			return;
		}
		if (!this.ngxSessionService.shouldDisplayOptionsMenu(this.session)) {
			return;
		}

		event.preventDefault();
		this.menuComponent.openMenu(event, this.cardContainer.nativeElement);
	}

	public getTooltip (session): string {
		if (session.isRecorded() && session.media?.isPending()) {
			return this.translateService.instant('session-card_processing');
		}
		// This has a newline in it, combined with a css
		// rule to preserve the new line in the tooltip.
		return `${session.description}

		${session.getFormattedPresenters()}`;
	}

	public cardAriaLabel () {
		const activity = this.selectedService.getActivity();
		const currentUser = this.userService.currentUser;

		let message = this.ngxSessionService.shouldDisplayJoinButton(this.session) ?
			this.translateService.instant('session-list-item_controller-join-live-session') :
			this.translateService.instant('session-list-item_controller-critique-video');

		if (!this.session.isRecorded() &&
			activity.is_conversation &&
			this.UADetect.browserDetector.isOldEdge()
		) {
			message = this.translateService.instant('common_unsupported-feature-try-latest-browser-version');
			// Currently only applicable to Multiple Camera activities
		} else if (!this.session.isRecorded() &&
			this.session.hasMaxPresenterCount(activity) &&
			!this.session.isPresenter(currentUser)) {
			message = this.translateService.instant('session-list-item_controller-max-presenter-count');
		} else if (this.session.source_media && this.session.source_media.isPending()) {
			message = this.translateService.instant('session-list-item_controller-source-media-not-finished-processing');
		} else if (this.session.media && this.session.media.isPending() && this.session.isRecorded()) {
			message = this.translateService.instant('session-list-item_controller-media-not-finished-processing');
		} else if (this.session.media && this.session.media.isFailed()) {
			message = this.translateService.instant('session-list-item_controller-media-processing-failed');
		}

		return message+ ',' + [this.getVideoThumbnailTitle(), this.getSessionViewedStateText(), this.getCommentAriaLabel()].join(',');
	}

	/**
	 * Get video thumbnail title for screenreader
	 *
	 * @returns {string}
	 */
	public getVideoThumbnailTitle () {
		const presentersText = this.session.presenters
			.map((presenter) => presenter.fullname)
			.join(', ');

		let translation = this.translateService.instant('session-list-item_controller-video-thumbnail-title', {
			presenters: presentersText,
			videoDescription: this.session.description
		});

		if (this.ngxSessionService.getMedia(this.session)?.isReady()
			&& this.session.isPosted()
			&& this.shouldDisplaySubmissionDate(this.session)) {
			translation += `, ${this.translateService.instant('session-list-item_controller-video-thumbnail-title-submitted', {
				submitDate: this.datePipe.transform(this.session.posted_at, 'fullDate')
			})}`;
		}

		if (this.session.num_comments > 0) {
			const translationKey = this.session.num_comments === 1 ? 'common_comment' : 'common_comments';
			translation += `, ${this.session.num_comments + ' ' + this.translateService.instant(translationKey)}`;
		}

		// Add info about slides if present
		if (this.hasSlidesEnabled() && this.session.source_media_id) {
			translation += `, ${this.translateService.instant('session-list-item_aria-with-slides')}`;
		}

		// Add info about attachments if present
		if (this.session.num_attachments > 0) {
			translation += `, ${this.translateService.instant('session-list-item_aria-with-attachments', { count: this.session.num_attachments })}`;
		}

		return translation;
	}

	/**
	 * Get the session's state's text whether it's viewed or unviewed.
	 */
	public getSessionViewedStateText () {
		if (this.session.viewed) {
			return this.translateService.instant('session-list-item_controller-viewed_state');
		}

		return this.translateService.instant('session-list-item_controller-unviewed_state');
	}

	public getCommentAriaLabel () {
		if (!this.session.num_comments) {
			return this.translateService.instant('comment-notifications-no-comments');
		} else if (!this.session.num_unviewed_comments) {
			return this.translateService.instant('comment-notifications-viewed');
		} else if (this.session.num_direct_unviewed_comments) {
			const key = this.session.num_direct_unviewed_comments === 1 ?
				this.translateService.instant('comment-notifications-unviewed-single') :
				this.translateService.instant('comment-notifications-unviewed-plural', {number: this.session.num_direct_unviewed_comments});
			return key;
		} else if (this.session.num_unviewed_comments) {
			const key = this.session.num_unviewed_comments === 1 ?
				this.translateService.instant('comment-notifications-unviewed-new-single'):
				this.translateService.instant('comment-notifications-unviewed-new-plural');
			return key;
		}
	}

	/**
	 * Whether sessions activity has slides enabled
	 *
	 * @returns {boolean}
	 */
	public hasSlidesEnabled () {
		return this.selectedService.getActivity().isSlidesEnabled();
	}

	public unviewedState (session): boolean {
		return !session.viewed;
	}

	public isSubmittedLate (session): boolean {
		return this.ngxSessionService.isSubmittedLate(session);
	}

	public shouldDisplaySubmissionDate (session): boolean {
		return this.ngxSessionService.shouldDisplaySubmissionDate(session);
	}

	public handleTruncatedChange (sessionDescriptionTruncated: boolean) {
		this.sessionDescriptionTruncated = sessionDescriptionTruncated;
		this.cdr.detectChanges();
	}
}
